/**
 * Client crash reporting.
 *
 * There was no client observability at all: `ErrorBoundary` called `console.error`, so a React crash
 * on a user's machine was invisible to us forever - you could only learn about it if someone told
 * you. This ships the report to OUR backend (`POST /v1/client-errors`, which logs it), not to a
 * third-party service, so no user data leaves the platform to a vendor nobody chose.
 *
 * Everything here is best-effort by design. A failure to report a failure must never surface to the
 * user, retry, or block anything.
 */

const ENDPOINT = import.meta.env.PROD ? '/v1/client-errors' : '/api/analysis/v1/client-errors';

/** Identical messages repeat: a render loop can throw thousands of times a second. */
const seen = new Set<string>();
const MAX_DISTINCT = 20;

export function reportClientError(
  error: unknown,
  extra: { componentStack?: string } = {},
): void {
  try {
    const err = error instanceof Error ? error : new Error(String(error));
    const message = err.message || 'Unknown error';
    // Dedupe on message + first stack frame: enough to separate distinct bugs, cheap enough to do on
    // a hot path, and it stops one broken component from flooding the log.
    const key = `${message}|${(err.stack ?? '').split('\n')[1] ?? ''}`;
    if (seen.has(key) || seen.size >= MAX_DISTINCT) return;
    seen.add(key);

    const payload = JSON.stringify({
      message,
      stack: err.stack,
      componentStack: extra.componentStack,
      // The path, not the full URL: a query string can carry a search term the user typed.
      path: typeof location !== 'undefined' ? location.pathname : undefined,
    });

    // No credentials: a crash report needs no session, and sending one would put a cookie into a
    // request that exists purely to carry a stack trace.
    void fetch(ENDPOINT, {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: payload,
      credentials: 'omit',
      keepalive: true,
    }).catch(() => undefined);
  } catch {
    // Reporting must never itself throw into the boundary that called it.
  }
}

/**
 * Catches what an ErrorBoundary structurally cannot: errors thrown outside React's render cycle -
 * event handlers, timers, and rejected promises nobody awaited.
 */
export function installGlobalErrorReporting(): void {
  if (typeof window === 'undefined') return;
  window.addEventListener('error', (e) => {
    reportClientError(e.error ?? new Error(e.message));
  });
  window.addEventListener('unhandledrejection', (e) => {
    reportClientError(e.reason ?? new Error('Unhandled promise rejection'));
  });
}
